import React, { Suspense, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { Loader, ViewportContext } from "@monorepo/components";
import { PhoneInput, StyledForm, StyledRegistrationLink, usePhone } from "@monorepo/components";
import { TSelectedPhoneCountryV2 } from "@monorepo/components/src/shared/PhoneInput/hooks/usePhone";
import { APPLE_STORE_APP_LINK, GOOGLE_PLAY_APP_LINK } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { errorTitle, isResponseErrorArray } from "@monorepo/helpers";
import { useUrlReferralHash } from "@monorepo/hooks";
import { useConfirmReferralMutation } from "@monorepo/services/api/referral";
import theme from "@monorepo/styles/theme";
import { ErrorCodeEnum } from "@monorepo/types";
import { Paper, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import * as Yup from "yup";

import RegistrationBanner from "components/RegistrationBanner/RegistrationBanner";

const STEPS = {
  phone: "PHONE",
  success: "SUCCESS",
};

const isMobileWeb = () => {
  const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(navigator.userAgent);
};

const getStoreLinkForOS = () => {
  var userAgent = navigator.userAgent || navigator.vendor;

  if (/android/i.test(userAgent)) {
    return GOOGLE_PLAY_APP_LINK;
  }

  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return APPLE_STORE_APP_LINK;
  }

  return undefined;
};

export const PHONE_VERIFICATION_SCHEMA = (selectedPhoneCountry: TSelectedPhoneCountryV2) =>
  Yup.object().shape({
    phone: Yup.string()
      .phone(selectedPhoneCountry.countryCode, "common.wrong_phone_format")
      .required("common.required"),
  });

export const Referred = () => {
  const { isMobile } = useContext(ViewportContext);
  const { selectedPhoneCountry, setSelectedPhoneCountry } = usePhone();
  const history = useHistory();
  const { t } = useTranslation();
  const hashParam = useUrlReferralHash();

  const [confirmReferral] = useConfirmReferralMutation();
  const [step, setStep] = useState(STEPS.phone);

  const methods = useForm({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(PHONE_VERIFICATION_SCHEMA(selectedPhoneCountry)),
    defaultValues: {
      phone: "",
    },
  });

  const onSubmit = async (data: { phone: string }) => {
    if (data.phone && hashParam) {
      confirmReferral({ hash: hashParam, phone: data.phone })
        .unwrap()
        .then(() => {
          setStep(STEPS.success);
        })
        .catch((err) => {
          if (isResponseErrorArray(err)) {
            const errorStatus = err[0]?.status;
            const errorDetail =
              parseInt(errorStatus) === ErrorCodeEnum.BAD_REQUEST ? t("referred.alreadyReferred") : errorTitle(err);
            methods.setError("phone", { message: errorDetail, type: errorStatus });
          }
        });
    }
  };

  const isMobileWebsite = isMobileWeb();
  const storeLink = getStoreLinkForOS();

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={1}
          sx={{
            display: "grid",
            justifyContent: "center",
            marginLeft: "auto",
            marginRight: "auto",
            justifyItems: "center",
            alignContent: {
              xs: "flex-start",
              md: "center",
            },
            width: "100%",
            maxWidth: {
              xs: "350px",
              md: "864px",
            },
            minHeight: {
              md: "530px",
            },
            gridTemplateColumns: {
              xs: "100%",
              md: "50% 50%",
            },
          }}
        >
          {!isMobile && <RegistrationBanner />}
          <Box
            sx={{
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(1),
              rowGap: theme.spacing(1),
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignContent: {
                xs: "stretch",
              },
              width: {
                xs: "310px",
                md: "360px",
              },
              textAlign: "center",
              height: "100%",
              minHeight: "605px",
            }}
          >
            <Typography
              sx={{ mb: theme.spacing(1), color: theme.palette.text.secondary }}
              variant={"h6"}
              component={"h2"}
            >
              {t("referred.header")}
            </Typography>
            <Box></Box>
            <Suspense fallback={<Loader />}>
              {step === STEPS.phone && (
                <FormProvider {...methods}>
                  <StyledForm sx={{ rowGap: 2 }} onSubmit={methods.handleSubmit(onSubmit)}>
                    <Box sx={{ mb: theme.spacing(7), alignSelf: "center", width: { xs: "auto", md: "320px" } }}>
                      <Typography
                        sx={{
                          color: theme.palette.text.secondary,
                          mb: theme.spacing(4),
                        }}
                      >
                        {t("referred.description")}
                      </Typography>
                      <PhoneInput
                        autoFocus
                        name="phone"
                        control={methods.control}
                        setSelectedPhoneCountry={setSelectedPhoneCountry}
                      />
                    </Box>
                    {methods.formState.errors.phone?.type === String(ErrorCodeEnum.BAD_REQUEST) && (
                      <StyledRegistrationLink
                        to={{
                          pathname: routeConfig.register.self,
                          search: "?phone=" + methods.getValues("phone")?.replace(/ /g, ""),
                        }}
                        sx={{
                          margin: { md: "0 auto" },
                          width: { xs: "auto", md: "320px" },
                          position: "relative",
                          top: theme.spacing(-10),
                        }}
                      >
                        {t("common.register")}
                      </StyledRegistrationLink>
                    )}
                    <Button
                      variant="contained"
                      sx={{ width: "100%", mt: "auto" }}
                      disableElevation
                      disabled={!methods.formState.isValid || methods.formState.isSubmitting}
                      onClick={() => methods.handleSubmit(onSubmit)}
                      type="submit"
                    >
                      {t("common.continue")}
                    </Button>
                    <Typography
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {t("referred.rules")}
                      <a
                        href="https://www.perlasgo.lt/rekomenduok-akcijos-taisykles"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("referred.rulesLink")}
                      </a>
                    </Typography>
                  </StyledForm>
                </FormProvider>
              )}
              {step === STEPS.success && (
                <>
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: theme.spacing(4),
                    }}
                  >
                    {t("referred.success")}
                  </Typography>
                  <Box
                    sx={{
                      mb: theme.spacing(7),
                      alignSelf: "center",
                      width: { xs: "auto", md: "320px" },
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{ width: "100%", mt: "auto", mb: theme.spacing(2) }}
                      disableElevation
                      disabled={!methods.formState.isValid || methods.formState.isSubmitting}
                      onClick={() => history.replace(`${routeConfig.register.self}?phone=${methods.getValues().phone}`)}
                      type="submit"
                    >
                      {t("referred.continueRegister")}
                    </Button>
                    {isMobileWebsite && storeLink && (
                      <Button
                        variant="contained"
                        sx={{ width: "100%", mt: "auto" }}
                        disableElevation
                        disabled={!methods.formState.isValid || methods.formState.isSubmitting}
                        onClick={() => window.location.replace(storeLink)}
                        type="submit"
                      >
                        {t("referred.continueWithApp")}
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </Suspense>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Referred;
